import { useEffect } from 'react'
import { Routes, Route, Outlet, useLocation, Navigate } from 'react-router-dom'
import { RequireRole } from './lib/auth'
import { seoFor, SITE_URL } from './lib/seo'
import Header from './components/Header'
import Footer from './components/Footer'

import Home from './pages/Home'
import CDHCI from './pages/CDHCI'
import Services from './pages/Services'
import ServiceDetail from './pages/ServiceDetail'
import SpecializedCare from './pages/SpecializedCare'
import HowItWorks from './pages/HowItWorks'
import About from './pages/About'
import Resources from './pages/Resources'
import FamilyRoom from './pages/FamilyRoom'
import Careers from './pages/Careers'
import Contact from './pages/Contact'
import ScheduleAssessment from './pages/ScheduleAssessment'
import Login from './pages/Login'
import SetPassword from './pages/SetPassword'
import Privacy from './pages/Privacy'
import Terms from './pages/Terms'
import Accessibility from './pages/Accessibility'
import AdminPortal from './pages/portal/AdminPortal'
import FamilyPortal from './pages/portal/FamilyPortal'
import CaregiverPortal from './pages/portal/CaregiverPortal'
import NotFound from './pages/NotFound'

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    // Must not return scrollTo's result: React would treat it as the cleanup
    // function and call it on the next navigation. Browser extensions that
    // patch scrollTo make it return a non-function, which crashes the app.
    try {
      window.scrollTo(0, 0)
    } catch {
      /* scroll position is cosmetic — never let it break navigation */
    }
  }, [pathname])
  return null
}

/**
 * The server injects the correct tags into the HTML it serves, which is what
 * crawlers read. This keeps the head honest for the rest of a visit, once
 * navigation happens on the client and no new document is fetched.
 */
function SeoTags() {
  const { pathname } = useLocation()
  useEffect(() => {
    const seo = seoFor(pathname)
    document.title = seo.title

    const set = (selector, attr, value) => {
      let el = document.head.querySelector(selector)
      if (!el) {
        el = document.createElement(selector.startsWith('link') ? 'link' : 'meta')
        const [, name, key] = selector.match(/\[(.+?)="(.+?)"\]/) || []
        if (name && key) el.setAttribute(name, key)
        document.head.appendChild(el)
      }
      el.setAttribute(attr, value)
    }

    set('meta[name="description"]', 'content', seo.description)
    set('meta[name="robots"]', 'content', seo.noindex ? 'noindex, nofollow' : 'index, follow')
    set('link[rel="canonical"]', 'href', seo.canonical)
    set('meta[property="og:title"]', 'content', seo.title)
    set('meta[property="og:description"]', 'content', seo.description)
    set('meta[property="og:url"]', 'content', seo.canonical)
    set('meta[property="og:image"]', 'content', `${SITE_URL}${seo.image}`)
    set('meta[name="twitter:title"]', 'content', seo.title)
    set('meta[name="twitter:description"]', 'content', seo.description)
    set('meta[name="twitter:image"]', 'content', `${SITE_URL}${seo.image}`)
  }, [pathname])
  return null
}

function Layout() {
  return (
    <>
      <Header />
      <main>
        <Outlet />
      </main>
      <Footer />
    </>
  )
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <SeoTags />
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="/specialized-care" element={<SpecializedCare />} />
          <Route path="/specialized-care/:slug" element={<ServiceDetail specialized />} />
          <Route path="/client-directed-home-care-invoicing" element={<CDHCI />} />
          {/* Short form people are likely to type or be told over the phone. */}
          <Route path="/cdhci" element={<Navigate to="/client-directed-home-care-invoicing" replace />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/about" element={<About />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/family-room" element={<FamilyRoom />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/schedule-assessment" element={<ScheduleAssessment />} />
          <Route path="/login" element={<Login />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/accessibility" element={<Accessibility />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route
            path="/portal/admin"
            element={<RequireRole role="admin"><AdminPortal /></RequireRole>}
          />
          <Route
            path="/portal/family"
            element={<RequireRole role="family"><FamilyPortal /></RequireRole>}
          />
          <Route
            path="/portal/caregiver"
            element={<RequireRole role="caregiver"><CaregiverPortal /></RequireRole>}
          />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </>
  )
}
